import type { ConversationResponse } from '@acme/api/admin';
import { SupportService as AdminSupportService } from '@acme/api/admin';
import type { CancelablePromise } from '@acme/api/shared';
import { OpenAPI, request } from '@acme/api/shared';
import type { SupportChatProps } from '@acme/domains';
import type { ChatWindowApi } from '@acme/features';
import { createEffect, createEvent, createStore, sample } from 'effector';

import { groupConversationsByDay } from '../lib';

export const pageOpened = createEvent();
export const refreshConversations = createEvent();

export const setSearchQuery = createEvent<string>();
export const setSelectedUserId = createEvent<string | undefined>();
export const selectConversation = createEvent<ConversationResponse>();

export const fetchConversationsFx = createEffect(
  async ({ search }: { search?: string }) => {
    const response = await AdminSupportService.getApiAdminV1SupportConversation({
      pageNumber: 1,
      pageSize: 50,
      search,
    });

    return response.data?.items ?? [];
  }
);

export const $searchQuery = createStore<string>('')
  .on(setSearchQuery, (_, query) => query)
  .reset(pageOpened);

const normalizeSearch = (query: string): string | undefined => {
  const trimmed = query.trim();
  if (trimmed.length === 0) return undefined;
  return trimmed;
};

const shouldSearch = (query: string) => {
  const trimmed = query.trim();
  return trimmed.length === 0 || trimmed.length >= 2;
};

export const $conversations = createStore<ConversationResponse[]>([])
  .on(fetchConversationsFx.doneData, (_, conversations) => conversations)
  .on(fetchConversationsFx.fail, () => []);

export const $isLoading = fetchConversationsFx.pending;

export const $error = createStore<string | null>(null)
  .on(fetchConversationsFx, () => null)
  .on(
    fetchConversationsFx.failData,
    () => 'Ошибка загрузки чатов. Попробуйте обновить страницу.'
  );

export const $selectedUserId = createStore<string | undefined>(undefined, {
  skipVoid: false,
})
  .on(setSelectedUserId, (_, userId) => userId)
  .on(
    selectConversation,
    (_, conversation) => conversation.userId ?? undefined
  );

export const $conversationSections = $conversations.map((conversations) =>
  groupConversationsByDay(conversations)
);

export const $selectedConversation = createStore<ConversationResponse | null>(
  null
);

export const $selectedUserName = $selectedConversation.map(
  (conversation) => conversation?.userName || 'Пользователь'
);

const getAttachmentReq: SupportChatProps['getAttachmentReq'] = ({ id }) => {
  void id;
  return Promise.reject<Blob>(
    new Error('Attachment download not implemented for admin')
  ) as unknown as CancelablePromise<Blob>;
};

export const $chatWindowApi = createStore<ChatWindowApi>({
  companionLabelText: 'Пользователь',
  loadChatHistory: () => {
    throw new Error('User ID is required');
  },
  sendMessage: () => {
    throw new Error('User ID is required');
  },
  getAttachmentReq,
}).on($selectedUserId, (_, selectedUserId) => ({
  companionLabelText: 'Пользователь',
  loadChatHistory: (params) => {
    if (!selectedUserId) {
      throw new Error('User ID is required');
    }

    return request(OpenAPI, {
      method: 'GET',
      url: '/api/admin/v1/support/conversation/{userId}/message',
      path: {
        userId: selectedUserId,
      },
      query: {
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      },
    });
  },
  sendMessage: (req) => {
    if (!selectedUserId) {
      throw new Error('User ID is required');
    }

    return AdminSupportService.postApiAdminV1SupportMessageSend({
      formData: {
        userId: selectedUserId,
        text: req.formData.text,
        files: req.formData.files,
      },
    });
  },
  getAttachmentReq,
}));

sample({
  clock: pageOpened,
  source: $searchQuery,
  fn: (searchQuery) => ({ search: normalizeSearch(searchQuery) }),
  target: fetchConversationsFx,
});

sample({
  clock: refreshConversations,
  source: $searchQuery,
  fn: (searchQuery) => ({ search: normalizeSearch(searchQuery) }),
  target: fetchConversationsFx,
});

sample({
  clock: setSearchQuery,
  filter: shouldSearch,
  fn: (searchQuery) => ({ search: normalizeSearch(searchQuery) }),
  target: fetchConversationsFx,
});

sample({
  clock: [$conversations, $selectedUserId],
  source: { conversations: $conversations, selectedUserId: $selectedUserId },
  fn: ({ conversations, selectedUserId }) => {
    if (!selectedUserId) return null;
    return conversations.find((c) => c.userId === selectedUserId) ?? null;
  },
  target: $selectedConversation,
});
